import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import logo from "../../../Assets/Images/logo.png";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import axios from "axios";
import { MAIN_URL, getApiHeaders } from "../../../Configurations/Urls";
import { Eye, EyeOff } from "lucide-react";
import { setClientPreferredOrganizationId } from "../../../Utils/clientActiveOrganization";

const SpinSVG = () => (
  <svg width="18" height="18" viewBox="0 0 15 15" fill="none"
    style={{ animation: "spin 0.8s linear infinite", flexShrink: 0 }}>
    <circle cx="7.5" cy="7.5" r="5.5" stroke="currentColor" strokeOpacity=".3" strokeWidth="2.5" fill="none" />
    <path d="M7.5 2A5.5 5.5 0 0 1 13 7.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" fill="none" />
  </svg>
);

function Login({
  title = "User Login",
  appName = "Your App Name",
  description,
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({});

  const [loading, setLoading] = useState(false);
  const [pendingSession, setPendingSession] = useState(null);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState("");

  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const savedEmail = localStorage.getItem("savedEmail");
    const savedPassword = localStorage.getItem("savedPassword");

    if (savedEmail && savedPassword) {
      setEmail(savedEmail);
      setPassword(savedPassword);
      setRememberMe(true);
    }
  }, []);

  const { isLoggedIn, login, logout, setAccess } = useAuthStore();
  const navigate = useNavigate();

  const getAllowedOrganizations = (user) => {
    const allOrganizations = user?.organization || [];
    const allowedOrgIds = new Set(
      (user?.metadata || [])
        .map((item) => Number(item?.organization_id))
        .filter(Boolean)
    );

    if (!allowedOrgIds.size) return allOrganizations;
    return allOrganizations.filter((org) =>
      allowedOrgIds.has(Number(org?.organization_id))
    );
  };

  const completeLoginForOrganization = async (
    organizationId,
    session = pendingSession
  ) => {
    if (!session?.user || !session?.token) return;

    const response = await axios.post(
      `${MAIN_URL}/api/organizations/extra/active`,
      {
        organization_id: Number(organizationId),
        application_user_id: session.user.application_user_id,
      },
      {
        headers: {
          Authorization: `Bearer ${session.token}`,
        },
      }
    );

    const { user } = response.data;
    const activeOrgId = user?.user_active_organization?.organization_id;

    const allOrgs = Array.isArray(session?.user?.organization) && session.user.organization.length > 0
      ? session.user.organization
      : Array.isArray(user?.organization)
        ? user.organization
        : [];

    const activeOrg = allOrgs.find(
      (item) => Number(item.organization_id) === Number(activeOrgId)
    ) || allOrgs[0] || null;

    setClientPreferredOrganizationId(Number(organizationId));

    login({
      ...user,
      organizations: allOrgs,
      organization: activeOrg,
    });
    toast.success("Login successful!");
    setPendingSession(null);
    setSelectedOrganizationId("");
    navigate("/");
  };

  const validateForm = () => {
    let formErrors = {};
    let isValid = true;

    if (!email) {
      formErrors.email = "Email is required";
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      formErrors.email = "Invalid email format";
      isValid = false;
    }

    if (!password) {
      formErrors.password = "Password is required";
      isValid = false;
    } else if (password.length < 6) {
      formErrors.password = "Password must be at least 6 characters";
      isValid = false;
    }

    setErrors(formErrors);
    return isValid;
  };

  const submitLogin = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (rememberMe) {
      localStorage.setItem("savedEmail", email);
      localStorage.setItem("savedPassword", password);
    } else {
      localStorage.removeItem("savedEmail");
      localStorage.removeItem("savedPassword");
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${MAIN_URL}/api/auth/login`,
        {
          email,
          password_hash: password,
        },
        { headers: getApiHeaders() }
      );
      const { token, user } = response.data;

      localStorage.setItem("token", token);
      setAccess(user?.role?.system_role_name);
      const organizations = getAllowedOrganizations(user);
      if (organizations.length > 1) {
        setPendingSession({ token, user: { ...user, organization: organizations } });
        setSelectedOrganizationId(
          String(
            organizations.some(
              (o) =>
                Number(o?.organization_id) ===
                Number(user?.user_active_organization?.organization_id)
            )
              ? user?.user_active_organization?.organization_id
              : organizations?.[0]?.organization_id || ""
          )
        );
        toast("Select organization to continue.");
      } else {
        const fallbackOrgId =
          (organizations.some(
            (o) =>
              Number(o?.organization_id) ===
              Number(user?.user_active_organization?.organization_id)
          )
            ? user?.user_active_organization?.organization_id
            : null) ||
          organizations?.[0]?.organization_id;
        await completeLoginForOrganization(fallbackOrgId, {
          token,
          user: { ...user, organization: organizations },
        });
      }
    } catch (error) {
      if (error.response && error.response.data) {
        const { errors, message } = error.response.data;
        if (errors) {
          setErrors(errors);
        } else {
          toast.error(message || "Login failed.");
        }
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (isLoggedIn && token) {
      navigate("/", { replace: true });
    } else if (isLoggedIn && !token) {
      logout();
    }
  }, [isLoggedIn, logout, navigate]);

  const organizations = pendingSession?.user?.organization || [];

  return (
    <div className="ems-auth-root">

      {/* ── Left panel — login card ── */}
      <div className="ems-auth-left">
        <div className="ems-auth-card">

          <div className="ems-auth-brand">
            <img src={logo} alt="Logo" className="ems-auth-brand-logo" />
          </div>

          <h1 className="ems-auth-h">Welcome back</h1>
          <p className="ems-auth-sub">Sign in to continue to {title}.</p>

          <form onSubmit={submitLogin} noValidate>
            <div className="ems-auth-field">
              <label htmlFor="auth-email">Email Address</label>
              <input
                id="auth-email"
                type="text"
                className="ems-fm-input"
                placeholder="name@company.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setErrors((p) => ({ ...p, email: "" })); }}
              />
              {errors.email && <span className="ems-fm-err">{errors.email}</span>}
            </div>

            <div className="ems-auth-field">
              <label htmlFor="auth-pw">Password</label>
              <div className="ems-auth-pw">
                <input
                  id="auth-pw"
                  type={showPassword ? "text" : "password"}
                  className="ems-fm-input"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setErrors((p) => ({ ...p, password: "" })); }}
                />
                <button
                  type="button"
                  className="ems-auth-pw-eye"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && <span className="ems-fm-err">{errors.password}</span>}
            </div>

            <div className="ems-auth-meta">
              <label className="ems-auth-remember">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                Remember me
              </label>
              <Link to="/forgot-password" className="ems-auth-forgot">Forgot password?</Link>
            </div>

            <div className="ems-auth-actions">
              <button type="submit" className="ems-btn ems-primary" disabled={loading}>
                {loading ? <><SpinSVG /> Signing in…</> : "Sign In"}
              </button>
              <button
                type="button"
                className="ems-btn"
                onClick={() => navigate("/start-free-trial")}
                disabled={loading}
              >
                Start Free Trial
              </button>
            </div>
          </form>

        </div>
      </div>

      {/* ── Right panel — brand hero ── */}
      <div className="ems-auth-right">
        <div className="ems-auth-hero">
          <div className="ems-auth-hero-mark">
            <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
              <path d="M5 8h16M5 13h10M5 18h16" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          </div>
          <h2 className="ems-auth-hero-h">{appName}</h2>
          <p className="ems-auth-hero-p">
            {description || "A powerful and flexible solution to manage your organization efficiently."}
          </p>
          <div className="ems-auth-trust">
            <span className="ems-auth-trust-dot" />
            Trusted by thousands of users.
          </div>
        </div>
        <p className="ems-auth-copy">&copy; {new Date().getFullYear()} {appName}. All rights reserved.</p>
      </div>

      {/* ── Org picker overlay ── */}
      {pendingSession && (
        <div className="ems-org-overlay">
          <div className="ems-org-picker">
            <div className="ems-org-picker-hd">
              <img src={logo} alt="Logo" style={{ height: 42 }} />
              <h3 className="ems-org-picker-h">Choose Organization</h3>
              <p className="ems-org-picker-sub">Select the organization you want to sign in to.</p>
            </div>

            <div className="ems-org-grid">
              {organizations.map((org) => {
                const isSelected = Number(selectedOrganizationId) === Number(org.organization_id);
                return (
                  <button
                    key={org.organization_id}
                    type="button"
                    className={"ems-org-btn" + (isSelected ? " selected" : "")}
                    onClick={() => setSelectedOrganizationId(String(org.organization_id))}
                  >
                    <div className="ems-org-btn-name">{org.organization_name}</div>
                    <div className="ems-org-btn-sub">
                      {org.organization_short_name || `Org ID: ${org.organization_id}`}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="ems-org-foot">
              <button
                type="button"
                className="ems-btn ems-ghost"
                onClick={() => { setPendingSession(null); setSelectedOrganizationId(""); }}
              >
                Back
              </button>
              <button
                type="button"
                className="ems-btn ems-primary"
                disabled={!selectedOrganizationId || loading}
                onClick={() => completeLoginForOrganization(selectedOrganizationId)}
              >
                {loading ? <><SpinSVG /> Signing in…</> : "Continue to Dashboard"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default Login;
